import { useSessionStore } from '@/lib/auth/session-store'
import {
  isAppLocale,
  setLocale,
  type AppLocale,
} from '@/lib/i18n'

import { authApi } from './api'

/**
 * Locale switch (D-038): persist locally, update the document language, and —
 * when authenticated — mirror the choice to `users.locale` through
 * `PATCH /me`. The profile value wins on the next session restore.
 *
 * Returns `false` when the local preference changed but the server sync
 * failed, so the switch can tell the user to retry without losing the UI
 * language.
 */
export async function changeLocalePreference(
  locale: AppLocale,
): Promise<boolean> {
  if (!isAppLocale(locale)) return false
  await setLocale(locale)
  return syncLocaleToProfile(locale)
}

/**
 * Mirrors an already-applied locale to `users.locale`. Used by the language
 * switcher after it has persisted and applied the local choice.
 */
export async function syncLocaleToProfile(
  locale: AppLocale,
): Promise<boolean> {
  const { status, accessToken } = useSessionStore.getState()
  if (status !== 'authenticated' || !accessToken) return true

  try {
    await authApi.updateProfile({ locale })
    return true
  } catch {
    return false
  }
}
